import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ProjectDetails.css';
import { useToast} from './ToastContext';

const ProjectEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [form, setForm] = useState({});
  const [existingImages, setExistingImages] = useState([]);
  const [removedImages, setRemovedImages] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [showCompleteForm, setShowCompleteForm] = useState(false);
  const [completionDateInput, setCompletionDateInput] = useState('');

  const API_BASE_URL = 'https://eks-projectmanager.eksconstruction.in/api';
  const BASE_URL = 'https://eks-projectmanager.eksconstruction.in';
  const { showToast, showConfirm } = useToast();



  useEffect(() => {
    const fetchProject = async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/get_projects.php`);
        const result = await resp.json();
        if (result.success && result.projects) {
          const found = result.projects.find(p => p.id === id || p.id === parseInt(id));
          if (found) {
            const mapped = {
              id: found.id,
              projectType: found.project_type,
              clientName: found.client_name,
              projectTitle: found.project_title,
              location: found.location,
              area: found.area,
              floors: found.floors,
              phase: found.phase,
              startDate: found.start_date,
              completionDate: found.completion_date,
              progress: found.progress,
              keyFeatures: found.key_features || '',
              images: found.images || [],
              createdAt: found.created_at
            };
            setProject(mapped);
            setForm({
              projectType: mapped.projectType,
              clientName: mapped.clientName,
              projectTitle: mapped.projectTitle,
              location: mapped.location,
              area: mapped.area,
              floors: mapped.floors,
              phase: mapped.phase,
              startDate: mapped.startDate,
              completionDate: mapped.completionDate,
              progress: mapped.progress,
              keyFeatures: mapped.keyFeatures
            });
            setExistingImages(mapped.images || []);
            setCompletionDateInput(mapped.completionDate || new Date().toISOString().slice(0,10));
          }
        }
      } catch (e) {
        console.error('Fetch project error', e);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchProject();
  }, [id]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setNewFiles(prev => prev.concat(files));
  };

  const removeExisting = async (img) => {
    try {
      const confirmed = await showConfirm('Remove this image? This will delete it when you save changes.', {
        title: 'Remove Image',
        confirmText: 'Remove',
        cancelText: 'Keep'
      });

      if (!confirmed) return;

      setExistingImages(prev => prev.filter(i => i !== img));
      setRemovedImages(prev => prev.concat([img]));
      showToast({ message: 'Image removed (will be deleted on save)', type: 'info' });
    } catch (error) {
      showToast({ message: 'Error removing image: ' + error.message, type: 'error' });
    }
  };

  const removeNewFile = (index) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  const buildAndSubmit = async (extra = {}) => {
    try {
      const fd = new FormData();
      fd.append('id', id);
      // append fields
      Object.keys(form).forEach(k => {
        if (form[k] !== undefined && form[k] !== null) fd.append(k, form[k]);
      });
      // removed images
      removedImages.forEach(img => fd.append('remove_images[]', img));
      // new files
      newFiles.forEach(f => fd.append('images[]', f));

      // extra (e.g., force complete)
      Object.keys(extra).forEach(k => {
        fd.append(k, extra[k]);
      });

      const resp = await fetch(`${API_BASE_URL}/update_data.php`, {
        method: 'POST',
        body: fd
      });
      const result = await resp.json();
      if (result.success) {
        // success - show toast and go back to dashboard
        showToast && showToast({ message: 'Project updated successfully', type: 'success' });
        navigate('/dashboard');
      } else {
        showToast && showToast({ message: 'Update failed: ' + (result.error || result.message || JSON.stringify(result)), type: 'error' });
      }
    } catch (e) {
      console.error('Update error', e);
      showToast && showToast({ message: 'Update error: ' + e.message, type: 'error' });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    await buildAndSubmit();
  };

  const handleMarkCompleted = async () => {
    const payload = {
      projectType: 'Completed Projects',
      progress: 100,
      completionDate: completionDateInput
    };
    try {
      const confirmed = await showConfirm('Mark this project as completed? This will set progress to 100.', {
        title: 'Complete Project',
        confirmText: 'Complete', 
        cancelText: 'Cancel'
      });
      
      if (!confirmed) return;
      await buildAndSubmit(payload);
    } catch (error) {
      showToast({ message: 'Error marking as completed: ' + error.message, type: 'error' });
    }
  };

  const handleMarkOngoing = async () => {
    // If currently 100, reduce to a sensible ongoing value (50), otherwise keep current progress
    const currentProgress = Number(form.progress || project.progress || 0);
    const newProgress = currentProgress >= 100 ? 50 : currentProgress;
    const payload = {
      projectType: 'Ongoing Projects',
      progress: newProgress
    };
    try {
      const confirmed = await showConfirm('Mark this project as ongoing? This will change project status to Ongoing.', {
        title: 'Change Status',
        confirmText: 'Change Status',
        cancelText: 'Cancel'
      });
      
      if (!confirmed) return;
      await buildAndSubmit(payload);
    } catch (error) {
      showToast({ message: 'Error marking as ongoing: ' + error.message, type: 'error' });
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading project details...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{padding:40}}>
        <p>Project not found</p>
        <button className="back-btn" onClick={() => navigate('/dashboard')}>Back</button>
      </div>
    );
  }

  return (
    <div className="project-details-container">
      <div className="details-header">
        <button className="back-btn" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </button>
      </div>

      <div className="details-content">
        <div className="details-card">
          <div className="details-project-header">
            <div className="header-top">
              <div>
                <h1 className="project-detail-title">Edit: {form.projectTitle || project.projectTitle}</h1>
                <p className="project-detail-client">{form.clientName || project.clientName}</p>
              </div>
            </div>
          </div>

          <div className="image-gallery">
            <div className="gallery-main">
              <img src={`${BASE_URL}${existingImages[0] || project.images[0] || ''}`} alt="main" style={{width:'100%', height:'100%', objectFit:'cover'}}/>
            </div>
            <div className="gallery-thumbnails">
              {existingImages.map((img, idx) => (
                <div key={idx} className="thumbnail">
                  <img src={`${BASE_URL}${img}`} alt={`img-${idx}`} />
                  <button className="remove-btn" onClick={() => removeExisting(img)}>Remove</button>
                </div>
              ))}

              {newFiles.map((f, idx) => (
                <div key={`new-${idx}`} className="thumbnail">
                  <img src={URL.createObjectURL(f)} alt={`new-${idx}`} />
                  <button className="remove-btn" onClick={() => removeNewFile(idx)}>Remove</button>
                </div>
              ))}

              <div style={{display:'flex', alignItems:'center', gap:8}}>
                <label className="upload-label" style={{cursor:'pointer'}}>
                  Upload
                  <input type="file" multiple onChange={handleFileChange} />
                </label>
              </div>
            </div>
          </div>

          <form style={{padding:40}} onSubmit={handleSave}>
            {/* Info Grid (location, area, floors, phase) */}
            <div className="info-grid">
              <div className="info-card">
                <div className="info-card-icon">📍</div>
                <div className="info-card-content">
                  <p className="info-card-label">Location</p>
                  <input value={form.location || ''} onChange={e => handleChange('location', e.target.value)} />
                </div>
              </div>

              <div className="info-card">
                <div className="info-card-icon">📏</div>
                <div className="info-card-content">
                  <p className="info-card-label">Area</p>
                  <input value={form.area || ''} onChange={e => handleChange('area', e.target.value)} />
                </div>
              </div>

              <div className="info-card">
                <div className="info-card-icon">🏢</div>
                <div className="info-card-content">
                  <p className="info-card-label">Floors/Units</p>
                  <input value={form.floors || ''} onChange={e => handleChange('floors', e.target.value)} />
                </div>
              </div>

              <div className="info-card">
                <div className="info-card-icon">⚙️</div>
                <div className="info-card-content">
                  <p className="info-card-label">Phase/Site</p>
                  <input value={form.phase || ''} onChange={e => handleChange('phase', e.target.value)} />
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="timeline-section">
              <h2 className="section-title">Project Timeline</h2>
              <div className="timeline">
                <div className="timeline-item">
                  <div className="timeline-marker start"></div>
                  <div className="timeline-content">
                    <p className="timeline-label">Start Date</p>
                    <input type="date" value={form.startDate || ''} onChange={e => handleChange('startDate', e.target.value)} />
                  </div>
                </div>
                <div className="timeline-line"></div>
                <div className="timeline-item">
                  <div className={`timeline-marker ${form.projectType === 'Completed Projects' ? 'completed' : 'end'}`}></div>
                  <div className="timeline-content">
                    <p className="timeline-label">Completion Date</p>
                    <input type="date" value={form.completionDate || ''} onChange={e => handleChange('completionDate', e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            {/* Progress - show only for Ongoing and Completed */}
            {( (form.projectType || project.projectType) === 'Ongoing Projects' || (form.projectType || project.projectType) === 'Completed Projects' ) && (
              <div className="progress-section">
                <h2 className="section-title">Project Progress</h2>
                <div className="progress-container">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={form.progress || 0}
                    onChange={e => handleChange('progress', e.target.value)}
                    className="progress-slider"
                    disabled={(form.projectType || project.projectType) === 'Completed Projects'}
                  />

                  <div className="progress-bar-bg">
                    <div
                      className={`progress-bar-fill ${(form.projectType || project.projectType) === 'Completed Projects' ? 'completed' : ''}`}
                      style={{ width: `${form.progress || 0}%` }}
                    >
                      <span className="progress-label">{form.progress || 0}%</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Key Features */}
            <div className="features-section">
              <h2 className="section-title">Key Features</h2>
              <div className="features-grid">
                <textarea value={form.keyFeatures || ''} onChange={e => handleChange('keyFeatures', e.target.value)} placeholder="Key features (comma separated)" />
              </div>
            </div>

            {/* Actions */}
            <div style={{display:'flex', gap:12, padding:20}}>
              <button className="back-btn mark-btn" type="submit">Save</button>

              {/* Decide which mark button to show based on current project type */}
              {((form.projectType || project.projectType) === 'Completed Projects') && (
                <button type="button" className="back-btn mark-btn" onClick={handleMarkOngoing}>Mark as Ongoing</button>
              )}

              {((form.projectType || project.projectType) === 'Ongoing Projects') && (
                <button type="button" className="back-btn mark-btn" onClick={() => setShowCompleteForm(s => !s)}>Mark as Completed</button>
              )}

              {((form.projectType || project.projectType) === 'Upcoming Projects') && (
                <button type="button" className="back-btn mark-btn" onClick={handleMarkOngoing}>Mark as Ongoing</button>
              )}
            </div>

            {showCompleteForm && (
              <div style={{padding:12, border:'1px solid #e2e8f0', borderRadius:8}}>
                <p>Enter completion details</p>
                <input type="date" value={completionDateInput} onChange={e => setCompletionDateInput(e.target.value)} />
                <div style={{marginTop:8}}>
                  <label className="upload-label" style={{cursor:'pointer'}}>
                    Upload final images
                    <input type="file" multiple onChange={(e) => { const files=Array.from(e.target.files||[]); setNewFiles(prev=>prev.concat(files)); }} />
                  </label>
                </div>
                <div style={{marginTop:12}}>
                  <button type="button" className="back-btn" onClick={handleMarkCompleted}>Confirm & Mark Completed</button>
                </div>
              </div>
            )}

          </form>
        </div>
      </div>
    </div>
  );
};

export default ProjectEdit;
